import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Door, DoorActions, DoorButton, DoorText } from "@optymyse/suite-ui";
import { centralUrl, signInUrl } from "../lib/hosts";
import { strings } from "../lib/strings";
import { PageTitle } from "../lib/title";

/**
 * Where Optymyse returns the browser after signing the person out of the suite (POST /auth/logout),
 * registered at Impa as Steer's post_logout_redirect_uri (Impa's PLATFORM_CONVENTIONS.md, rule 9).
 * Signed out of every app, the person goes straight on to sign in, as Vercel, Linear, Atlassian and
 * Slack do: Impa's session has ended, so Optymyse asks for the password and signs nobody back in. The
 * Worker sends a browser on before this page is drawn (src/worker/index.ts); this does the same for a
 * page that comes here itself.
 *
 * A sign-out that couldn't reach Optymyse stays here, as the suite's door: Steer alone is signed out,
 * and a sign-in would pass straight through Impa's live session, so it waits to be asked.
 */
function SignedOutPage() {
  // Set by the page itself when Steer couldn't get Optymyse's sign-out address (lib/auth.ts).
  const { impa } = Route.useSearch();
  const unreached = impa === "unavailable";
  useEffect(() => {
    if (!unreached) window.location.replace(signInUrl(centralUrl("/admin")));
  }, [unreached]);
  if (!unreached) return null;
  return (
    <Door product="steer" title={strings.auth.signedOut.unreachedTitle} pip="warning">
      <PageTitle name={strings.auth.signedOut.unreachedTitle} />
      <DoorText>{strings.auth.signedOut.unreachedBody}</DoorText>
      <DoorActions>
        <DoorButton asChild>
          <a href={signInUrl(centralUrl("/admin"))}>{strings.auth.signedOut.signInAgain}</a>
        </DoorButton>
      </DoorActions>
    </Door>
  );
}

export const Route = createFileRoute("/signed-out")({
  validateSearch: (search: Record<string, unknown>): { impa?: "unavailable" } =>
    search.impa === "unavailable" ? { impa: "unavailable" } : {},
  component: SignedOutPage,
});
